import React, { useState } from "react";
import { PrivacyPolicySection } from "./styles";
import { Box, Container, Divider, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";

type Lang = "EN" | "AR";

const PrivacyPolicy = () => {
	const [lang, setLang] = useState<Lang>("AR");
	const isAr = lang === "AR";

	return (
		<Box pt={4}>
			<PrivacyPolicySection dir={isAr ? "rtl" : "ltr"}>
				<Container maxWidth={"md"}>
					<Box display={"flex"} justifyContent={"flex-end"} mb={2}>
						<ToggleButtonGroup
							size={"small"}
							value={lang}
							exclusive
							onChange={(_, value) => value && setLang(value)}
							aria-label={"language toggle"}
							style={{ direction: "ltr" }}
						>
							<ToggleButton value={"EN"}>EN</ToggleButton>
							<ToggleButton value={"AR"}>AR</ToggleButton>
						</ToggleButtonGroup>
					</Box>
					<Box display={"flex"} flexDirection={"column"}>
						<Typography variant={"h2"} className={"page-title"}>
							{isAr ? "سياسة الخصوصية" : "Privacy Policy"}
						</Typography>
						<Typography className={"effective-date"}>
							{isAr ? "تاريخ السريان: 14 يونيو 2026" : "Effective Date: June 14, 2026"}
						</Typography>

						<Typography className={"policy-intro"}>
							{isAr ? (
								<>
									في <strong>VitesLink</strong> ("نحن" أو "لنا")، نُقدّر خصوصيتك ونلتزم بحماية بياناتك الشخصية. توضح
									سياسة الخصوصية هذه كيفية جمعنا لمعلوماتك واستخدامها وتخزينها وحمايتها عند استخدامك لمنصتنا الرقمية
									لإنشاء الدعوات.
									<br />
									<br />
									باستخدامك VitesLink، فإنك توافق على الممارسات الموضحة في سياسة الخصوصية هذه.
								</>
							) : (
								<>
									At <strong>VitesLink</strong> ("we", "our", or "us"), we value your privacy and are committed to
									protecting your personal information. This Privacy Policy explains how we collect, use, store, and
									safeguard your information when you use our digital invitation platform.
									<br />
									<br />
									By using VitesLink, you agree to the practices described in this Privacy Policy.
								</>
							)}
						</Typography>

						<Divider sx={{ mb: 4 }} />

						{/* Section 1 */}
						<Box className={"section"}>
							<Typography className={"section-title"}>
								{isAr ? "1. المعلومات التي نجمعها" : "1. Information We Collect"}
							</Typography>

							<Typography className={"sub-title"}>{isAr ? "بيانات الحساب" : "Account Information"}</Typography>
							<Typography className={"section-body"}>
								{isAr
									? "عند إنشاء حساب أو استخدام خدماتنا، قد نقوم بجمع:"
									: "When you create an account or use our services, we may collect:"}
							</Typography>
							<ul>
								{isAr ? (
									<>
										<li>الاسم</li>
										<li>البريد الإلكتروني</li>
										<li>رقم الهاتف (إن تم تقديمه)</li>
										<li>بيانات الفوترة (تتم معالجتها بأمان عبر مزودي الدفع)</li>
									</>
								) : (
									<>
										<li>Name</li>
										<li>Email address</li>
										<li>Phone number (if provided)</li>
										<li>Billing details (processed securely via payment providers)</li>
									</>
								)}
							</ul>

							<Typography className={"sub-title"}>{isAr ? "بيانات الدعوة" : "Invitation Information"}</Typography>
							<Typography className={"section-body"}>
								{isAr
									? "لإنشاء دعوتك وإدارتها، قد نجمع المحتوى الذي تقدّمه، بما في ذلك:"
									: "To generate and manage your invitation, we may collect content you provide, including:"}
							</Typography>
							<ul>
								{isAr ? (
									<>
										<li>الأسماء</li>
										<li>تاريخ وتفاصيل المناسبة</li>
										<li>معلومات المكان</li>
										<li>الصور والوسائط المرفوعة</li>
										<li>الرسائل المخصصة ومحتوى تصميم الدعوة</li>
									</>
								) : (
									<>
										<li>Names</li>
										<li>Event date and details</li>
										<li>Venue information</li>
										<li>Photos and media uploads</li>
										<li>Custom messages and invitation design content</li>
									</>
								)}
							</ul>

							<Typography className={"sub-title"}>
								{isAr ? "بيانات الضيوف وتأكيد الحضور" : "Guest & RSVP Information"}
							</Typography>
							<Typography className={"section-body"}>
								{isAr
									? "في حال تفعيل ميزات تأكيد الحضور (RSVP)، قد نجمع:"
									: "If RSVP features are enabled, we may collect:"}
							</Typography>
							<ul>
								{isAr ? (
									<>
										<li>أسماء الضيوف</li>
										<li>ردود تأكيد الحضور (حاضر / غير حاضر)</li>
										<li>تفاصيل الحضور</li>
										<li>رسائل اختيارية من الضيوف</li>
									</>
								) : (
									<>
										<li>Guest names</li>
										<li>RSVP responses (attending / not attending)</li>
										<li>Attendance details</li>
										<li>Optional guest messages</li>
									</>
								)}
							</ul>

							<Typography className={"sub-title"}>{isAr ? "بيانات الدفع" : "Payment Information"}</Typography>
							<Typography className={"section-body"}>
								{isAr
									? "تتم معالجة المدفوعات بأمان عبر مزودي دفع خارجيين موثوقين."
									: "Payments are processed securely through third-party payment providers."}
							</Typography>
							<ul>
								{isAr ? (
									<>
										<li>لا نقوم بتخزين بيانات البطاقات الائتمانية/الخصم الكاملة على خوادمنا.</li>
										<li>يتولى معالجو الدفع الموثوقون التعامل مع بيانات دفعك بأمان.</li>
									</>
								) : (
									<>
										<li>We do not store full credit/debit card details on our servers.</li>
										<li>Trusted payment processors handle your payment data securely.</li>
									</>
								)}
							</ul>

							<Typography className={"sub-title"}>
								{isAr ? "البيانات التقنية وبيانات الاستخدام" : "Technical & Usage Information"}
							</Typography>
							<Typography className={"section-body"}>
								{isAr ? "قد نجمع تلقائيًا:" : "We may automatically collect:"}
							</Typography>
							<ul>
								{isAr ? (
									<>
										<li>عنوان IP</li>
										<li>نوع الجهاز والمتصفح</li>
										<li>نظام التشغيل</li>
										<li>الصفحات التي تمت زيارتها وسلوك الاستخدام</li>
										<li>ملفات تعريف الارتباط (الكوكيز) وتقنيات التتبع المشابهة</li>
									</>
								) : (
									<>
										<li>IP address</li>
										<li>Device type and browser</li>
										<li>Operating system</li>
										<li>Pages visited and usage behavior</li>
										<li>Cookies and similar tracking technologies</li>
									</>
								)}
							</ul>
							<Typography className={"section-body"} mt={1}>
								{isAr ? (
									<>
										كما نستخدم <strong>Google Analytics</strong> لفهم كيفية استخدام الموقع وتحسين أدائه، و
										<strong>Meta Pixel</strong> لقياس أداء الإعلانات وتحسين الحملات التسويقية.
									</>
								) : (
									<>
										We also use <strong>Google Analytics</strong> to understand website usage and improve performance,
										and <strong>Meta Pixel</strong> to measure advertising performance and optimize marketing campaigns.
									</>
								)}
							</Typography>
						</Box>

						{/* Section 2 */}
						<Box className={"section"}>
							<Typography className={"section-title"}>
								{isAr ? "2. كيف نستخدم بياناتك" : "2. How We Use Your Information"}
							</Typography>
							<Typography className={"section-body"}>
								{isAr ? "نستخدم البيانات التي نجمعها من أجل:" : "We use collected information to:"}
							</Typography>
							<ul>
								{isAr ? (
									<>
										<li>إنشاء دعوتك وإدارتها</li>
										<li>توفير وظيفة تأكيد الحضور (RSVP)</li>
										<li>معالجة المدفوعات وإدارة الطلبات</li>
										<li>تحسين أداء المنصة وتجربة المستخدم</li>
										<li>تقديم الدعم الفني</li>
										<li>إرسال إشعارات مهمة متعلقة بالخدمة</li>
										<li>مراقبة الاستخدام ومنع الاحتيال وضمان أمان المنصة</li>
										<li>إجراء التحليلات وتحسين التسويق عبر Google Analytics وMeta Pixel</li>
									</>
								) : (
									<>
										<li>Create and manage your invitation</li>
										<li>Provide RSVP functionality</li>
										<li>Process payments and handle orders</li>
										<li>Improve platform performance and user experience</li>
										<li>Provide customer support</li>
										<li>Send important service-related notifications</li>
										<li>Monitor usage, prevent fraud, and ensure platform security</li>
										<li>Run analytics and marketing optimization through Google Analytics and Meta Pixel</li>
									</>
								)}
							</ul>
						</Box>

						{/* Section 3 */}
						<Box className={"section"}>
							<Typography className={"section-title"}>
								{isAr ? "3. مشاركة المعلومات" : "3. Sharing of Information"}
							</Typography>
							<Typography className={"section-body"}>
								{isAr ? (
									<>
										لا نبيع بياناتك الشخصية.
										<br />
										<br />
										قد نشارك معلومات محدودة مع مزودي خدمة خارجيين موثوقين، بما في ذلك:
									</>
								) : (
									<>
										We do not sell your personal data.
										<br />
										<br />
										We may share limited information with trusted third-party service providers, including:
									</>
								)}
							</Typography>
							<ul>
								{isAr ? (
									<>
										<li>مزود خدمة الدفع</li>
										<li>مزودو الاستضافة السحابية</li>
										<li>
											<strong>Google Analytics</strong> (تحليلات الاستخدام)
										</li>
										<li>
											<strong>Meta (فيسبوك / إنستغرام)</strong> عبر Meta Pixel (تتبع الإعلانات والأداء)
										</li>
										<li>أدوات دعم العملاء والمراقبة</li>
									</>
								) : (
									<>
										<li>Payment processor</li>
										<li>Cloud hosting providers</li>
										<li>
											<strong>Google Analytics</strong> (usage analytics)
										</li>
										<li>
											<strong>Meta (Facebook / Instagram)</strong> via Meta Pixel (advertising and performance tracking)
										</li>
										<li>Customer support and monitoring tools</li>
									</>
								)}
							</ul>
							<Typography className={"section-body"} mt={1}>
								{isAr
									? "لا تقوم هذه الجهات بمعالجة سوى البيانات اللازمة لأداء خدماتها."
									: "These providers only process data necessary to perform their services."}
							</Typography>
						</Box>

						{/* Section 4 */}
						<Box className={"section"}>
							<Typography className={"section-title"}>{isAr ? "4. خصوصية الضيوف" : "4. Guest Privacy"}</Typography>
							<Typography className={"section-body"}>
								{isAr ? (
									<>
										إذا قمت بإضافة بيانات ضيوف أو تفعيل ميزات تأكيد الحضور، فإنك تقر بأنك حصلت على إذن بمشاركة هذه
										البيانات معنا.
										<br />
										<br />
										تُستخدم بيانات الضيوف فقط لتقديم وظائف الدعوة وتأكيد الحضور.
									</>
								) : (
									<>
										If you add guest information or enable RSVP features, you confirm that you have obtained permission
										to share such data with us.
										<br />
										<br />
										Guest data is used only to provide invitation and RSVP functionality.
									</>
								)}
							</Typography>
						</Box>

						{/* Section 5 */}
						<Box className={"section"}>
							<Typography className={"section-title"}>
								{isAr ? "5. الاحتفاظ بالبيانات" : "5. Data Retention"}
							</Typography>
							<Typography className={"section-body"}>
								{isAr
									? "نحتفظ ببياناتك فقط طالما كان ذلك ضروريًا من أجل:"
									: "We retain your information only as long as necessary to:"}
							</Typography>
							<ul>
								{isAr ? (
									<>
										<li>تقديم خدماتنا</li>
										<li>الامتثال للالتزامات القانونية</li>
										<li>تسوية النزاعات</li>
										<li>الحفاظ على سلامة المنصة</li>
									</>
								) : (
									<>
										<li>Provide our services</li>
										<li>Comply with legal obligations</li>
										<li>Resolve disputes</li>
										<li>Maintain platform integrity</li>
									</>
								)}
							</ul>
							<Typography className={"section-body"} mt={1}>
								{isAr
									? "قد تُحذف الدعوات غير المنشورة أو التي في حالة مسودة بعد فترة من عدم النشاط."
									: "Unpublished or draft invitations may be deleted after a period of inactivity."}
							</Typography>
						</Box>

						{/* Section 6 */}
						<Box className={"section"}>
							<Typography className={"section-title"}>{isAr ? "6. أمن البيانات" : "6. Data Security"}</Typography>
							<Typography className={"section-body"}>
								{isAr ? (
									<>
										نطبّق تدابير أمنية تقنية وتنظيمية معقولة لحماية بياناتك.
										<br />
										<br />
										مع ذلك، لا يوجد نظام آمن بشكل كامل، ولا يمكننا ضمان الحماية المطلقة للمعلومات المرسلة عبر الإنترنت.
									</>
								) : (
									<>
										We implement reasonable technical and organizational security measures to protect your data.
										<br />
										<br />
										However, no system is completely secure, and we cannot guarantee absolute protection of information
										transmitted online.
									</>
								)}
							</Typography>
						</Box>

						{/* Section 7 */}
						<Box className={"section"}>
							<Typography className={"section-title"}>
								{isAr ? "7. ملفات تعريف الارتباط وتقنيات التتبع" : "7. Cookies & Tracking Technologies"}
							</Typography>
							<Typography className={"section-body"}>
								{isAr
									? "نستخدم ملفات تعريف الارتباط (الكوكيز) وتقنيات مشابهة من أجل:"
									: "We use cookies and similar technologies to:"}
							</Typography>
							<ul>
								{isAr ? (
									<>
										<li>تفعيل وظائف المنصة</li>
										<li>تذكّر تفضيلات المستخدم</li>
										<li>تحليل أداء الموقع</li>
										<li>تشغيل حملات التسويق والإعلان</li>
									</>
								) : (
									<>
										<li>Enable platform functionality</li>
										<li>Remember user preferences</li>
										<li>Analyze website performance</li>
										<li>Run marketing and advertising campaigns</li>
									</>
								)}
							</ul>
							<Typography className={"section-body"} mt={1}>
								{isAr
									? "يشمل ذلك ملفات تعريف الارتباط الخاصة بـ Google Analytics وMeta Pixel. يمكنك تعطيل ملفات تعريف الارتباط من إعدادات متصفحك، إلا أن بعض الميزات قد لا تعمل بشكل صحيح."
									: "This includes cookies from Google Analytics and Meta Pixel. You may disable cookies through your browser settings, but some features may not work properly."}
							</Typography>
						</Box>

						{/* Section 8 */}
						<Box className={"section"}>
							<Typography className={"section-title"}>{isAr ? "8. حقوقك" : "8. Your Rights"}</Typography>
							<Typography className={"section-body"}>
								{isAr ? "بحسب موقعك، قد يكون لديك الحق في:" : "Depending on your location, you may have rights to:"}
							</Typography>
							<ul>
								{isAr ? (
									<>
										<li>الوصول إلى بياناتك الشخصية</li>
										<li>طلب تصحيح المعلومات غير الدقيقة</li>
										<li>طلب حذف بياناتك</li>
										<li>سحب موافقتك حيثما ينطبق ذلك</li>
									</>
								) : (
									<>
										<li>Access your personal data</li>
										<li>Request correction of inaccurate information</li>
										<li>Request deletion of your data</li>
										<li>Withdraw consent where applicable</li>
									</>
								)}
							</ul>
							<Typography className={"section-body"} mt={1}>
								{isAr ? (
									<>
										لممارسة هذه الحقوق، يُرجى التواصل معنا عبر{" "}
										<a href="mailto:info@viteslink.com">info@viteslink.com</a>.
									</>
								) : (
									<>
										To exercise these rights, please contact us at{" "}
										<a href="mailto:info@viteslink.com">info@viteslink.com</a>.
									</>
								)}
							</Typography>
						</Box>

						{/* Section 9 */}
						<Box className={"section"}>
							<Typography className={"section-title"}>
								{isAr ? "9. خصوصية الأطفال" : "9. Children's Privacy"}
							</Typography>
							<Typography className={"section-body"}>
								{isAr
									? "VitesLink غير مخصصة للأفراد دون سن 18 عامًا، ولا نقوم عن قصد بجمع بيانات من الأطفال."
									: "VitesLink is not intended for individuals under the age of 18, and we do not knowingly collect data from children."}
							</Typography>
						</Box>

						{/* Section 10 */}
						<Box className={"section"}>
							<Typography className={"section-title"}>
								{isAr ? "10. تعديلات على سياسة الخصوصية هذه" : "10. Changes to This Privacy Policy"}
							</Typography>
							<Typography className={"section-body"}>
								{isAr
									? "قد نقوم بتحديث سياسة الخصوصية هذه من وقت لآخر. سيتم نشر التعديلات على هذه الصفحة مع تحديث تاريخ السريان. استمرارك في استخدام المنصة بعد أي تحديث يُعد موافقة منك على السياسة المعدّلة."
									: "We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated effective date. Continued use of the platform after any update constitutes your acceptance of the revised policy."}
							</Typography>
						</Box>

						{/* Section 11 */}
						<Box className={"section"}>
							<Typography className={"section-title"}>{isAr ? "11. تواصل معنا" : "11. Contact Us"}</Typography>
							<Typography className={"section-body"} mb={1}>
								{isAr
									? "إذا كانت لديك أي أسئلة حول سياسة الخصوصية هذه أو كيفية تعاملنا مع بياناتك، يُرجى التواصل مع فريق الدعم لدينا:"
									: "If you have any questions about this Privacy Policy or how we handle your data, please contact our support team:"}
							</Typography>
							<Box className={"contact-box"}>
								<Typography fontSize={"14px"} color={"#555"}>
									{isAr ? "البريد الإلكتروني:" : "Email:"} <a href="mailto:info@viteslink.com">info@viteslink.com</a>
								</Typography>
							</Box>
						</Box>
					</Box>
				</Container>
			</PrivacyPolicySection>
		</Box>
	);
};

export default PrivacyPolicy;
